import { Observer, observer } from 'mobx-react-lite'
import { action } from 'mobx'
import {
  RadioGroup,
  type RadioGroupProps,
} from '@/components/inputs/RadioGroup'
import { bindSelect } from '@/infra/view-binding'
import type { FilterProps } from '@/infra/type-utils'
import {
  type ContainerProps,
  FormFieldContainer,
  useFormFieldContainer,
} from '@/components/FormContainer'
import {
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
} from '../Form'
import cx from 'classnames'
import styles from './RadioContainer.module.css'

export const RadioContainer = observer(function RadioContainer<
  O extends object,
  M extends object,
  K extends keyof FilterProps<M, string | null>,
>({
  name,
  model,
  label,
  hint,
  field,
  error,
  hintTestId,
  'aria-label': ariaLabel,
  items,
  getKey,
  getLabel,
  ...rest
}: ContainerProps<
  M,
  string | null,
  K,
  Omit<RadioGroupProps<O>, 'value' | 'onChange' | 'isDisabled' | 'isInvalid'>
>) {
  return (
    <FormFieldContainer
      name={name}
      model={model}
      label={label}
      hint={hint}
      field={field}
      error={error}
      aria-label={ariaLabel}
      hintTestId={hintTestId}
      render={({ disabled, invalid, ...controlProps }) => (
        <Observer>
          {() => (
            <RadioGroup<O>
              {...controlProps}
              {...bindSelect(model, field)}
              {...rest}
              isDisabled={disabled}
              isInvalid={invalid}
              items={items}
              getKey={getKey}
              getLabel={getLabel}
            />
          )}
        </Observer>
      )}
    />
  )
})

interface BooleanItems {
  id: 'no' | 'yes'
  label: string
}

const NO_YES_ITEMS: BooleanItems[] = [
  { id: 'no', label: 'No' },
  { id: 'yes', label: 'Yes' },
] as const

export const RadioBooleanContainer = observer(function RadioBooleanContainer<
  M extends object,
  K extends keyof FilterProps<M, boolean | null>,
>({
  name,
  model,
  label,
  hint,
  field,
  error,
  hintTestId,
  'aria-label': ariaLabel,
  ...rest
}: ContainerProps<
  M,
  boolean | null,
  K,
  Omit<
    RadioGroupProps<BooleanItems>,
    'items' | 'getKey' | 'getLabel' | 'value' | 'isDisabled' | 'isInvalid'
  >
>) {
  const {
    controlProps: { disabled, invalid, ...controlProps },
    errorContent,
    errorProps,
    hintProps,
    hintContent,
    labelProps,
  } = useFormFieldContainer({
    name,
    model,
    field,
    hint,
    error,
    'aria-label': ariaLabel,
  })

  return (
    <FormField>
      <div
        className={cx(
          styles.radioGroupContainer,
          rest.orientation === 'horizontal'
            ? styles.horizontal
            : styles.vertical,
        )}
      >
        {label && <FormFieldLabel {...labelProps}>{label}</FormFieldLabel>}
        <RadioGroup<BooleanItems>
          {...controlProps}
          {...rest}
          isDisabled={disabled}
          isInvalid={invalid}
          items={NO_YES_ITEMS}
          getKey={(item) => item.id}
          getLabel={(item) => item.label}
          value={
            model[field] === true
              ? 'yes'
              : model[field] === false
                ? 'no'
                : undefined
          }
          onChange={action(`change ${String(field)}`, (value) => {
            ;(model[field] as boolean) = value === 'yes'
          })}
        />
      </div>
      <FormFieldHint testId={hintTestId} {...hintProps}>
        {hintContent}
      </FormFieldHint>
      <FormFieldError testId={hintTestId} {...errorProps}>
        {errorContent}
      </FormFieldError>
    </FormField>
  )
})
